import { useCallback, useEffect, useRef, useState } from "react";
import SwiperCore from "swiper";

import { PRODUCT_SLUG } from "@types";
import { ICardTab } from "types/card-tab";

interface UseDesktopCourseSwiperParams {
  slug: PRODUCT_SLUG;
  activeTab: ICardTab | null;
  onReachEnd?: () => void;
  isLoading?: boolean;
  firstLoad?: boolean;
  loadedProduct?: boolean;
}

const useDesktopCourseSwiper = ({
  slug,
  activeTab,
  onReachEnd,
  isLoading,
  firstLoad,
  loadedProduct,
}: UseDesktopCourseSwiperParams) => {
  const swiperRef = useRef<SwiperCore | null>(null);
  const [isEnd, setIsEnd] = useState(false);

  const products = activeTab?.products ?? [];
  const productsLength = products.length;
  const showCourse = productsLength > 0 && Boolean(loadedProduct);
  const uniqueSlug = `${slug}-desktop`;

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper || swiper.destroyed) return;
    if (!isLoading && productsLength && !firstLoad) {
      swiper.update();
      swiper.slideNext();
    }
  }, [isLoading, productsLength, firstLoad]);

  const syncEndState = useCallback((swiper?: SwiperCore) => {
    setIsEnd(Boolean(swiper?.isEnd ?? swiperRef.current?.isEnd));
  }, []);

  const handleSwiper = useCallback((swiper: SwiperCore) => {
    swiperRef.current = swiper;
  }, []);

  const handleReachEnd = useCallback(() => {
    syncEndState(swiperRef.current ?? undefined);
  }, [syncEndState]);

  const handleSlideChange = useCallback(
    (swiper: SwiperCore) => {
      syncEndState(swiper);
    },
    [syncEndState]
  );

  const handleFromEdge = useCallback(() => {
    setIsEnd(false);
  }, []);

  const handleNavigation = useCallback(() => {
    if (isLoading) return;
    const swiper = swiperRef.current;
    if (!swiper || swiper.destroyed) return;

    if (swiper.isEnd) {
      onReachEnd?.();
      return;
    }

    swiper.slideNext();
  }, [isLoading, onReachEnd]);

  return {
    uniqueSlug,
    showCourse,
    products,
    isEnd,
    handleSwiper,
    handleReachEnd,
    handleSlideChange,
    handleFromEdge,
    handleNavigation,
  };
};

export default useDesktopCourseSwiper;
